"use client";
import React from "react";
import { PartialPartner } from "@/common/partner/types";
import Image from "next/image";
import Link from "next/link";
import { partnerImageDimensions } from "@/constants/hero/imageDimensions";
import { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import { programMap } from "@/lib/program/constants";
import EmailOptIn from "@/components/EmailOptIn/EmailOptIn";
import useEmailOptIn from "../../../context/ProgramDetailContext/hooks/useEmailOptIn";
import ProgramEnrollButton from "../../../components/ProgramEnrollButton";
import type { ProcessedCourse } from "@/common/course/types";
import type { Program } from "@/common/program/types";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import { Skeleton } from "@/components/ui/skeleton";
import ProgramPricing from "@/components/ProgramDetailLayout/templates/components/ProgramPricing";
import { isValidPartnerLogoOverride } from "../../utils/partnerLogoOverrideValidation";
import CountdownBanner from "@/components/CountdownBanner";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ProgramRedesignTrafficTestResult } from "@/monarch/rules/programRedesignTraffic/types";
import DynamicBadges from "@/components/ProgramDetailLayout/templates/components/TaggedHeader/DynamicBadges";
import ProgramRetiredMessage from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage";
import {
  PROGRAM_RETIRED_MESSAGE,
  PROGRAM_UNAVAILABLE_MESSAGE,
} from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage/constants";
import HeroAccentCallout from "@/components/GeoPricing/HeroAccentCallout";
import { ProgramCurrencyInlinePill } from "@/components/ProgramDetailLayout/templates/components/ProgramCurrencySurfaces";
import { resolveCurrencySurfaces } from "@/lib/currency/resolveCurrencySurfaces";
import useTranslations from "@/i18n/useTranslations";

const VARIANT_STYLES = {
  light: {
    bg: "bg-putty-100",
    text: "text-[#002626]",
    badge: "bg-putty-400 border-putty-700",
    badgeText: "text-black",
    border: "border-putty-400",
  },
  dark: {
    bg: "bg-primary-500",
    text: "text-white",
    badge: "bg-[#BEF4E5] border-[#004f4f]",
    badgeText: "text-primary-500",
    border: "border-[#003838]",
  },
};

export interface ProgramHeaderProps {
  courses: ProcessedCourse[];
  programData: Program;
  skus: string[];
  pricingData: ProgramPricingData | null;
  duration: string;
  programIsEnrollable: boolean;
  programIsRetired?: boolean;
  variant?: "dark" | "light";
  showSideRail?: boolean;
}

const ProgramHeaderLoader: React.FC = () => (
  <div className="bg-white rounded-xl py-6 px-4 sm:p-10 md:flex md:items-start md:justify-between">
    <div className="flex flex-col gap-5 w-full">
      <div>
        <div className="flex justify-center sm:justify-start">
          <Skeleton className="h-16 w-40 rounded" />
        </div>

        <Skeleton className="h-4 w-32 mt-4" />

        <Skeleton className="h-8 w-60 mt-2" />
      </div>

      <div className="[&_p]:m-0 flex gap-2">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-16" />
      </div>

      <div className="flex xl:justify-end gap-2">
        <Skeleton className="h-5 w-5 rounded-full" />
        <Skeleton className="h-5 w-40" />
      </div>

      <div className="flex items-baseline w-full gap-2 justify-end">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-40" />
      </div>

      <div className="hidden md:flex justify-start items-center">
        <Skeleton className="h-5 w-80" />
      </div>
    </div>
  </div>
);

const ProgramHeader = ({
  courses,
  programData,
  skus,
  pricingData,
  duration,
  programIsEnrollable,
  programIsRetired = false,
  showSideRail,
  variant = "light",
}: ProgramHeaderProps) => {
  const tCommon = useTranslations("Common");
  const {
    isLoading: isHeaderLoading,
    headerCta,
    showHeaderPrice,
    showHeaderPriceMobile,
    showHeaderFactoidBar,
    showDynamicBadges,
    insertBadges,
    currencySelector,
  } = useProgramRedesignConfig();
  const currencySurfaces = resolveCurrencySurfaces(currencySelector);
  const showHeroCurrencySelector = !showSideRail && currencySurfaces.hero;

  const { emailOptIn, setEmailOptIn } = useEmailOptIn();
  const supTitle =
    programMap[programData.productType]?.type || programMap["default"].type;

  const showRetiredMessage =
    programIsRetired ||
    Boolean(programData.customizations?.catalogPage?.retiringSoon) ||
    !programData.programEligibleForOneClickPurchase;
  const retiredMessage = programIsRetired
    ? PROGRAM_RETIRED_MESSAGE
    : PROGRAM_UNAVAILABLE_MESSAGE;
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  if (isHeaderLoading) {
    return <ProgramHeaderLoader />;
  }
  return (
    <div
      className={`bg-white rounded-xl py-6 px-4 sm:p-10 md:flex md:items-start md:justify-between ${showSideRail ? "hidden" : ""}`}
      id="program-detail-header"
    >
      <div className="flex flex-col gap-5 w-full">
        <FeatureFlagComponentResolverClient
          flagKey="NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER"
          variants={{
            test: (
              <MonarchDataConsumer<ProgramRedesignTrafficTestResult>
                experimentKey="useProgramRedesignTraffic"
                render={(testResult) => {
                  const countdownBanner = testResult?.data?.countdownBanner;
                  return (
                    <div className="w-full md:w-auto text-center">
                      <CountdownBanner
                        variant="micro"
                        theme={countdownBanner?.hero?.theme || "light"}
                        header={countdownBanner?.hero?.text || ""}
                        targetDate={countdownBanner?.hero?.date || ""}
                        htmlId="hero-program-promo-countdown-banner"
                        location="program-detail-page"
                        name="hero-program-promo-countdown-banner"
                        className="justify-center"
                      />
                    </div>
                  );
                }}
              />
            ),
            legacy: <></>,
          }}
          fallback="legacy"
        />
        <div>
          <div className="flex justify-center sm:justify-start">
            {isValidPartnerLogoOverride(programData.partnerLogoOverride) ? (
              <Image
                src={programData.partnerLogoOverride}
                width={partnerImageDimensions.width}
                height={partnerImageDimensions.height}
                alt={tCommon("partnerLogoFor", {
                  title: programData.title || tCommon("thisProgram"),
                })}
                title={programData.title || ""}
                className="max-w-[165px] md:max-w-full"
              />
            ) : (
              programData.partners.map((partner: PartialPartner) => {
                return partner.showOnSchoolsAndPartnersPage ? (
                  <Link
                    className="d-inline-block"
                    key={partner.key}
                    href={`/school/${partner.slug}`}
                  >
                    <Image
                      priority
                      src={partner.logoUrl || ""}
                      width={partnerImageDimensions.width}
                      height={partnerImageDimensions.height}
                      alt={programData.title || ""}
                      title={programData.title || ""}
                      key={partner.key}
                      className="max-w-[165px] md:max-w-full"
                    />
                  </Link>
                ) : (
                  <Image
                    priority
                    src={partner.logoUrl || ""}
                    width={partnerImageDimensions.width}
                    height={partnerImageDimensions.height}
                    alt={programData.title || ""}
                    title={programData.title || ""}
                    key={partner.key}
                    className="max-w-[165px] md:max-w-full"
                  />
                );
              })
            )}
          </div>
          {!showSideRail && (
            <p className="mb-5 md:mb-4 text-sm md:text-base"> {supTitle}</p>
          )}
          <h1 className="m-0 p-0 tracking-tight" style={{ fontWeight: 900 }}>
            {programData.title}
          </h1>
        </div>

        <div className="[&_p]:m-0 flex">
          <a href="#courses">
            <p className="text-lg md:text-2xl underline font-bold">
              {programData.courses.length} courses
            </p>
          </a>
          <p className="text-lg md:text-2xl font-bold">, {duration}</p>
        </div>
        <HeroAccentCallout
          pricingData={pricingData}
          variant={variant}
          className="mt-4"
          location="program-detail-page"
        />
        {!showHeaderFactoidBar && (
          <div className="flex flex-col gap-5">
            {insertBadges && (
              <DynamicBadges
                courseData={courses}
                styles={{
                  badge: styles.badge,
                  badgeText: styles.badgeText,
                  margin: "!m-0",
                }}
                insertBadges={insertBadges}
              />
            )}
            {showDynamicBadges && (
              <DynamicBadges
                courseData={courses}
                styles={{
                  badge: styles.badge,
                  badgeText: styles.badgeText,
                  margin: "!m-0",
                }}
              />
            )}
          </div>
        )}

        {showRetiredMessage && (
          <ProgramRetiredMessage
            className="xl:justify-end text-primary"
            textClassName="text-black"
            message={retiredMessage}
          />
        )}

        <div
          className={`flex ${showHeroCurrencySelector ? "items-center" : "items-baseline"}  w-full gap-5 ${showHeaderPriceMobile ? "flex-wrap" : ""} ${
            headerCta?.pillPosition !== "right"
              ? "justify-start"
              : "justify-end"
          }`}
        >
          {pricingData && !showRetiredMessage && (
            <div
              className={`flex flex-col align-items-center justify-start md:justify-center ${showHeroCurrencySelector || showHeaderPriceMobile ? "w-full" : "hidden"} ${showHeroCurrencySelector || showHeaderPrice ? "md:w-auto md:flex" : "md:hidden"} gap-2 text-lg text-gray-800 ${showHeroCurrencySelector || showSideRail ? "order-2 ml-2" : ""}`}
            >
              <div className="flex flex-row items-center gap-2">
                {showHeroCurrencySelector ? (
                  <ProgramCurrencyInlinePill
                    pricingData={pricingData}
                    location="program-pdp-hero"
                    variant="default"
                    showPrice
                  />
                ) : (
                  <ProgramPricing
                    pricingData={pricingData}
                    strikethroughClass="line-through text-gray-500 opacity-100"
                    stackBigPrices={showSideRail ? false : true}
                  />
                )}
              </div>
            </div>
          )}
          {!showRetiredMessage &&
            programIsEnrollable &&
            programData.programEligibleForOneClickPurchase && (
              <div className="flex flex-col md:flex-row gap-4 sm:gap-2 items-center bg-putty-100 p-4 rounded-xl border-putty-400 border w-full md:w-auto">
                <ProgramEnrollButton
                  courses={courses}
                  programData={programData}
                  skus={skus}
                />
              </div>
            )}
        </div>
        <div className="hidden md:flex justify-start items-center">
          <EmailOptIn
            title={programData.title || ""}
            partners={programData.partners}
            optIn={emailOptIn}
            variant="secondary"
            toggleEmailOptIn={() => {
              setEmailOptIn(!emailOptIn);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProgramHeader;
